import React from 'react';
import { Outlet } from 'react-router-dom';

/*
 * Product design shell — v3, the daylight editorial voice (ratified 2026-08-29).
 * Light-first: warm paper ground, ink Spectral display, wide-tracked slate caps,
 * gold as the single emphasis. Mirrors the `.dl3` scoped-token pattern from
 * src/pages/DesignLanguage.tsx with its own `.pd3` scope.
 *
 * Two layers:
 *   .pd3           — the deck palette as page-scoped custom properties.
 *   .dark .pd3     — the same names mapped onto the canonical dark ramp, so the
 *                    section still renders correctly when the app is in dark mode.
 *
 * The app's light palette is now the same warm paper (2026-10-01), so shared
 * components (ds/ tables, doc primitives) already sit correctly on this ground;
 * the block that used to remap the product tokens here is gone. It had pinned
 * --mustard to the display gold #A87400 (3.89:1 as small text) and --text-dim to
 * #9AA1B0 (2.48:1) on these pages only.
 */
export const PD3_CSS = `
.pd3 {
  --p-paper:      #FAFAF7;
  --p-shade:      #F5F3EC;
  --p-cream:      #FAF6EA;
  --p-hairline:   #E3E1D6;
  --p-hairline-2: #D6D3C6;
  --p-ink:        #1E2A44;
  --p-ink-soft:   #3E4A63;
  --p-label:      #5A6E8C;
  --p-faint:      #667080;
  --p-gold:       #A87400;
  --p-forest:     #3E6B52;
  --p-clay:       #B05A48;
}
.dark .pd3 {
  --p-paper:      var(--bg);
  --p-shade:      var(--surface);
  --p-cream:      #14120A;
  --p-hairline:   var(--rule);
  --p-hairline-2: var(--rule-bright);
  --p-ink:        var(--text-pri);
  --p-ink-soft:   var(--text-sec);
  --p-label:      var(--text-ter);
  --p-faint:      var(--text-dim);
  --p-gold:       var(--mustard);
  --p-forest:     var(--green);
  --p-clay:       var(--coral);
}
.pd3 { background: var(--p-paper); color: var(--p-ink); }
.pd3 .eyebrow { font-family: 'JetBrains Mono', monospace; font-size: 13px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--p-label); font-weight: 500; }
.pd3 .goldi { font-style: italic; color: var(--p-gold); }
.pd3 .pd3-card { transition: border-color 0.15s, background 0.15s; }
.pd3 .pd3-card:hover { border-color: var(--p-hairline-2); background: var(--p-paper); }
.pd3 .pd3-card .go { opacity: 0; transform: translateX(-2px); transition: opacity 0.15s, transform 0.15s; }
.pd3 .pd3-card:hover .go { opacity: 1; transform: translateX(0); }
@media (prefers-reduced-motion: reduce) {
  .pd3 .pd3-card, .pd3 .pd3-card .go { transition: none; }
}
`;

export const PrioritiesAIDesignLayout: React.FC = () => {
  return (
    <div className="pd3 w-full min-h-[calc(100vh-52px)] font-body">
      <style>{PD3_CSS}</style>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </div>
    </div>
  );
};
